import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import {
  RefreshCw,
  Radar,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const PAGE_SIZE = 50;

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

// Humanized timestamp: relative for the last 24h, full locale string beyond.
const fmtWhen = (iso, t) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 0) return d.toLocaleString();
  if (secs < 60) return t('just now');
  if (secs < 3600) return t('{n}m ago', { n: Math.floor(secs / 60) });
  if (secs < 86400) return t('{n}h ago', { n: Math.floor(secs / 3600) });
  return d.toLocaleString();
};

// destination is `describe_destination` shape: { type, target } where target
// is a string, an array of ids, or null. Types are data tokens (offer,
// landing, redirect, no_match, …) — shown raw, like the traffic simulator.
const fmtDestination = (dest) => {
  if (!dest || !dest.type) return '—';
  const target = Array.isArray(dest.target) ? dest.target.join(', ') : dest.target;
  return target ? `${dest.type} → ${target}` : dest.type;
};

const rateToPercentString = (rate) => {
  const r = typeof rate === 'number' && !Number.isNaN(rate) ? rate : 1;
  return String(parseFloat((Math.min(1, Math.max(0, r)) * 100).toFixed(2)));
};

export default function ClickTraces() {
  const { t } = useT();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState({});
  // Control-card state (mirrors the selected campaign's saved settings).
  const [traceEnabled, setTraceEnabled] = useState(false);
  const [sampleRatePct, setSampleRatePct] = useState('100');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(API + '/campaigns')
      .then((res) => setCampaigns(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setCampaigns([]);
        toast.error(t('Failed to load campaigns'));
      });
  }, []);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === campaignId) || null,
    [campaigns, campaignId]
  );

  // Sync the control card whenever the selection (or the saved campaign) changes.
  useEffect(() => {
    if (!selectedCampaign) return;
    setTraceEnabled(!!selectedCampaign.live_trace_enabled);
    setSampleRatePct(rateToPercentString(selectedCampaign.live_trace_sample_rate));
  }, [selectedCampaign]);

  const fetchTraces = useCallback(async () => {
    if (!campaignId) {
      setItems([]);
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      setLoadError(false);
      const res = await axios.get(`${API}/campaigns/${campaignId}/click-traces`, {
        params: { page, page_size: PAGE_SIZE },
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Error loading click traces:', err);
      setLoadError(true);
      toast.error(t('Error loading click traces'));
    } finally {
      setLoading(false);
    }
  }, [campaignId, page, t]);

  useEffect(() => {
    fetchTraces();
  }, [fetchTraces]);

  // Reset paging + collapsed rows when switching campaigns.
  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [campaignId]);

  const saveTraceSettings = async () => {
    if (!campaignId) return;
    const pct = parseFloat(sampleRatePct);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error(t('Sample rate must be between 0 and 100'));
      return;
    }
    const rate = Math.min(1, Math.max(0, pct / 100));
    try {
      setSaving(true);
      // Partial update — only the two live-trace fields, like the split-testing toggle.
      await axios.put(`${API}/campaigns/${campaignId}`, {
        live_trace_enabled: traceEnabled,
        live_trace_sample_rate: rate,
      });
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === campaignId
            ? { ...c, live_trace_enabled: traceEnabled, live_trace_sample_rate: rate }
            : c
        )
      );
      toast.success(t('Trace settings saved'));
    } catch (err) {
      console.error('Error saving trace settings:', err);
      toast.error(t('Failed to update campaign'));
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const tracingOn = !!selectedCampaign?.live_trace_enabled;
  // New campaigns get the journal for their first days (plan stage 3).
  const autoUntil = tracingOn ? selectedCampaign?.live_trace_auto_until : null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Diagnostics')}
        title={t('Click traces')}
        subtitle={t('Sampled real clicks with the full routing decision behind each one — which streams were evaluated and why the winner matched.')}
        actions={
          <Button variant="outline" onClick={fetchTraces} disabled={!campaignId || loading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            {t('Refresh')}
          </Button>
        }
      />

      <div className="w-72">
        <Select value={campaignId} onValueChange={setCampaignId}>
          <SelectTrigger aria-label={t('Campaign')}>
            <SelectValue placeholder={t('Select a campaign')} />
          </SelectTrigger>
          <SelectContent>
            {campaigns.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!campaignId ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty
              icon={Radar}
              title={t('Select a campaign')}
              subtitle={t('Pick a campaign above to inspect where its real clicks were routed and why.')}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Radar className="w-4 h-4" />
                {t('Live-click tracing')}
              </CardTitle>
              <CardDescription>
                {t('Off by default. When enabled, a sampled share of real clicks records its full routing trace. Privacy-safe: no IP addresses or user agents are stored, and traces expire automatically.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-6">
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={traceEnabled}
                    onCheckedChange={setTraceEnabled}
                    aria-label={t('Enable live-click tracing')}
                  />
                  <span className="text-sm">{t('Enable live-click tracing')}</span>
                </div>
                <div>
                  <label
                    htmlFor="trace-sample-rate"
                    className="block text-xs text-muted-foreground mb-1"
                  >
                    {t('Sample rate (%)')}
                  </label>
                  <Input
                    id="trace-sample-rate"
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    className="w-28"
                    value={sampleRatePct}
                    onChange={(e) => setSampleRatePct(e.target.value)}
                  />
                </div>
                <Button onClick={saveTraceSettings} disabled={saving}>
                  {saving ? t('Saving…') : t('Save')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {t('Share of clicks to trace, from 0 to 100%. Example: 10% traces roughly one click in ten.')}
              </p>
            </CardContent>
          </Card>

          {loading && items.length === 0 ? (
            <div className="skeleton h-96 w-full"></div>
          ) : loadError && items.length === 0 ? (
            <Card>
              <CardContent className="py-10">
                <XpnsrEmpty
                  icon={XCircle}
                  title={t('Could not load traces')}
                  subtitle={t('Something went wrong while loading click traces for this campaign.')}
                  cta={
                    <Button variant="outline" onClick={fetchTraces}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('Retry')}
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          ) : items.length === 0 ? (
            <Card>
              <CardContent className="py-10">
                <XpnsrEmpty
                  icon={Radar}
                  title={t('No traces yet')}
                  subtitle={
                    tracingOn
                      ? `${autoUntil ? t('Turned on automatically for the first days of the campaign, until {date}. Switch it on yourself to keep it.', { date: fmtTime(autoUntil) }) + ' ' : ''}${t('Tracing is on — traces of sampled real clicks will appear here as traffic arrives. If nothing shows up, check the sample rate.')}`
                      : t('Enable tracing above; traces of sampled real clicks will appear here.')
                  }
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>{t('Time')}</TableHead>
                      <TableHead>{t('Country')}</TableHead>
                      <TableHead>{t('Device')}</TableHead>
                      <TableHead>{t('Flags')}</TableHead>
                      <TableHead>{t('Stream')}</TableHead>
                      <TableHead>{t('Destination')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((row, idx) => {
                      const key = row.id || row.tracking_id || 'row-' + idx;
                      const isOpen = !!expanded[key];
                      const verdicts = Array.isArray(row.trace) ? row.trace : [];
                      return (
                        <React.Fragment key={key}>
                          <TableRow>
                            <TableCell className="w-10">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => toggleRow(key)}
                                aria-expanded={isOpen}
                                aria-label={isOpen ? t('Hide routing trace') : t('Show routing trace')}
                                title={isOpen ? t('Hide routing trace') : t('Show routing trace')}
                              >
                                {isOpen ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell
                              className="text-sm text-muted-foreground whitespace-nowrap"
                              title={fmtTime(row.created_at)}
                            >
                              {fmtWhen(row.created_at, t)}
                            </TableCell>
                            <TableCell>
                              {row.country_code ? (
                                <Badge variant="outline">{row.country_code}</Badge>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{row.device_type || '—'}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {row.is_bot ? (
                                  <Badge variant="destructive">{t('Bot')}</Badge>
                                ) : null}
                                {row.is_unique ? (
                                  <Badge variant="success">{t('Unique')}</Badge>
                                ) : null}
                                {!row.is_bot && !row.is_unique ? (
                                  <span className="text-sm text-muted-foreground">—</span>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              {row.stream_name || t('(no match)')}
                            </TableCell>
                            <TableCell
                              className="text-xs text-muted-foreground max-w-[20rem] truncate"
                              title={fmtDestination(row.destination)}
                            >
                              {fmtDestination(row.destination)}
                            </TableCell>
                          </TableRow>
                          {isOpen ? (
                            <TableRow className="bg-muted/5 hover:bg-muted/5">
                              <TableCell colSpan={7} className="py-3">
                                <div className="px-2">
                                  <div className="text-xs font-medium text-muted-foreground mb-2">
                                    {t('Per-stream verdicts')}
                                  </div>
                                  {verdicts.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">
                                      {t('No per-stream verdicts were recorded for this click.')}
                                    </p>
                                  ) : (
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-left text-muted-foreground">
                                          <th className="py-1 pr-2 font-medium">#</th>
                                          <th className="py-1 pr-2 font-medium">{t('Stream')}</th>
                                          <th className="py-1 pr-2 font-medium">{t('Type')}</th>
                                          <th className="py-1 pr-2 font-medium">{t('Matched')}</th>
                                          <th className="py-1 pr-2 font-medium">{t('Filters evaluated')}</th>
                                          <th className="py-1 pr-2 font-medium">{t('Relation')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {verdicts.map((s, i) => (
                                          <tr
                                            key={(s.stream_id || 'v') + '-' + i}
                                            className={
                                              'border-t border-border/50 align-top' +
                                              (s.matched ? ' font-medium' : '')
                                            }
                                          >
                                            <td className="py-1 pr-2 tabular-nums">
                                              {typeof s.position === 'number' ? s.position : i + 1}
                                            </td>
                                            <td className="py-1 pr-2">{s.name || s.stream_id || '—'}</td>
                                            <td className="py-1 pr-2">
                                              <Badge variant="outline">{s.stream_type || 'normal'}</Badge>
                                            </td>
                                            <td className="py-1 pr-2">
                                              {s.matched ? (
                                                <span
                                                  className="inline-flex items-center text-emerald-600"
                                                  role="img"
                                                  aria-label={t('Matched')}
                                                  title={t('Matched')}
                                                >
                                                  <CheckCircle2 className="w-4 h-4" />
                                                </span>
                                              ) : (
                                                <span
                                                  className="inline-flex items-center text-rose-600"
                                                  role="img"
                                                  aria-label={t('Not matched')}
                                                  title={t('Not matched')}
                                                >
                                                  <XCircle className="w-4 h-4" />
                                                </span>
                                              )}
                                            </td>
                                            <td className="py-1 pr-2 tabular-nums">
                                              {typeof s.filters_evaluated === 'number'
                                                ? s.filters_evaluated
                                                : 0}
                                            </td>
                                            <td className="py-1 pr-2 uppercase">
                                              {s.filter_relation || 'and'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {total > PAGE_SIZE ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t('Page')} {page} / {totalPages} · {total} {t('entries')}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t('Previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('Next')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
